// Sidebar — CRM left rail.
//
// Phase 1 renders exactly 5 Simple nav items + Settings, plus an optional
// "Расширенные (Pro)" section that is gated by the `pro-mode` feature flag.
// When the flag is OFF the section does not render at all; when ON it
// renders its heading + a stub body so Week 4+ can drop Scenarios / Runs
// entries in without further layout churn.
//
// Ported structurally from erp2 `components/layout/Sidebar.tsx` (dense list,
// active-state accent) but stripped of all GovardOS couplings (window.api,
// settingsStore, flatmedia overview, OCStatusIndicator, lucide-react). Icons
// are inline SVGs so we don't pull a new dependency for six glyphs.

import { useMemo, useState, type ReactElement, type SVGProps } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { cn } from '@govard/ui-primitives';
import { useTranslation } from '@govard/i18n-core/react';
import { useFeatureFlag } from '@govard/feature-flags/react';
import { ChevronLeft, Menu as MenuIcon } from 'lucide-react';

import { CRM_FLAG_IDS } from '../../feature-flags/index.js';

type IconComponent = (props: SVGProps<SVGSVGElement>) => ReactElement;

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconComponent;
}

const SIMPLE_NAV: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'crm.nav.dashboard', icon: IconDashboard },
  { to: '/deals', labelKey: 'crm.nav.deals', icon: IconDeals },
  { to: '/contacts', labelKey: 'crm.nav.contacts', icon: IconContacts },
  { to: '/documents', labelKey: 'Документы', icon: IconDocuments },
  { to: '/templates', labelKey: 'crm.nav.templates', icon: IconTemplates },
  { to: '/hot-leads', labelKey: 'crm.nav.hot_leads', icon: IconHotLeads },
  { to: '/settings', labelKey: 'crm.nav.settings', icon: IconSettings },
] as const;

const PRO_NAV: readonly NavItem[] = [
  { to: '/memory', labelKey: 'crm.nav.memory', icon: IconMemory },
  { to: '/run-center', labelKey: 'crm.nav.run_center', icon: IconRunCenter },
  { to: '/scenario-editor', labelKey: 'crm.nav.scenario_editor', icon: IconScenario },
  { to: '/prompt-compiler', labelKey: 'crm.nav.prompt_compiler', icon: IconPromptCompiler },
  { to: '/knowledge', labelKey: 'crm.nav.knowledge_base', icon: IconKnowledge },
  { to: '/files', labelKey: 'crm.nav.files', icon: IconFiles },
  { to: '/agents', labelKey: 'crm.nav.agents', icon: IconAgents },
  { to: '/opencode', labelKey: 'crm.nav.opencode', icon: IconOpencode },
] as const;

export function Sidebar(): ReactElement {
  const { t } = useTranslation();
  const proMode = useFeatureFlag(CRM_FLAG_IDS.PRO_MODE);
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();

  const simpleItems = useMemo(() => SIMPLE_NAV, []);

  // Compute selected keys for Ant Design Menu based on closest match
  const selectedKeys = useMemo(() => {
    const allPaths = [...SIMPLE_NAV.map(i => i.to), ...(proMode ? PRO_NAV.map(i => i.to) : [])];
    const match = allPaths.find(path => location.pathname.startsWith(path) && path !== '/dashboard') 
      || (location.pathname === '/dashboard' ? '/dashboard' : '');
    return match ? [match] : [];
  }, [location.pathname, proMode]);

  const menuItems: MenuProps['items'] = [
    ...(isExpanded ? [{ type: 'group' as const, label: 'GENERAL', key: 'group-general' }] : []),
    ...simpleItems.map(item => ({
      key: item.to,
      icon: <item.icon className="h-4 w-4" style={{ display: 'inline-flex', verticalAlign: 'middle' }} />,
      label: <NavLink to={item.to} end={item.to === '/dashboard'}>{t(item.labelKey)}</NavLink>,
    })),
    ...(proMode ? [
      { type: 'divider' as const },
      ...(isExpanded ? [{ type: 'group' as const, label: 'ADVANCED', key: 'group-advanced' }] : []),
      ...PRO_NAV.map(item => ({
        key: item.to,
        icon: <item.icon className="h-4 w-4" style={{ display: 'inline-flex', verticalAlign: 'middle' }} />,
        label: <NavLink to={item.to}>{t(item.labelKey)}</NavLink>,
      }))
    ] : [])
  ];

  return (
    <aside
      aria-label={t('crm.sidebar.brand')}
      className="flex h-full shrink-0 flex-col border-r transition-all duration-300 ease-in-out"
      style={{
        width: isExpanded ? 'var(--sidebar-width, 250px)' : '72px',
        backgroundColor: '#fffdfa', // 'var(--color-bg-secondary, #f5f1eb)'
        borderRight: '1px solid #e7e5e4',
      }}
    >
      <div
        className={cn(
          'flex shrink-0 items-center border-b',
          isExpanded ? 'justify-between px-5' : 'justify-center',
        )}
        style={{
          height: '64px', // Standard antd layout header height
          borderBottom: '1px solid #e7e5e4',
        }}
      >
        <div className="flex items-center justify-center gap-3 overflow-hidden">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-bold text-white shadow-sm"
            style={{ backgroundColor: '#d97706', fontSize: 13 }}
          >
            G
          </span>
          {isExpanded ? (
            <span className="truncate text-sm font-bold text-[#44403c] tracking-tight">
              GovardOS
            </span>
          ) : null}
        </div>
      </div>

      <div
        className="hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden flex flex-col"
        style={{ padding: '16px 0' }}
      >
        <div className={cn('mb-2 flex w-full', isExpanded ? 'justify-end pr-4 pl-4' : 'justify-center')}>
          <button
            type="button"
            onClick={() => setIsExpanded((value) => !value)}
            className="rounded-lg p-2 transition-colors hover:bg-orange-50 hover:text-amber-600 focus:outline-none"
            aria-label={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
            title={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
            style={{ color: '#a8a29e' }}
          >
            {isExpanded ? <ChevronLeft size={20} /> : <MenuIcon size={20} />}
          </button>
        </div>

        <Menu
          mode="inline"
          inlineCollapsed={!isExpanded}
          items={menuItems}
          selectedKeys={selectedKeys}
          style={{ borderRight: 'none', backgroundColor: 'transparent' }}
          className="custom-sidebar-menu mt-2"
        />
      </div>

      <div
        className={cn(
          'w-full shrink-0 border-t',
          isExpanded
            ? 'flex items-center justify-between px-5'
            : 'flex flex-col items-center justify-center',
        )}
        style={{
          borderColor: '#e7e5e4',
          padding: '16px 0',
          gap: 12,
          minHeight: 76,
        }}
      >
        <button
          type="button"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold transition-transform hover:scale-105"
          style={{
            backgroundColor: '#fffbeb',
            color: '#b45309',
            fontSize: 12,
            border: '1px solid #fde68a'
          }}
          title={isExpanded ? undefined : 'Admin Profile'}
        >
          AD
        </button>
        {isExpanded ? (
          <div className="ml-3 flex flex-1 flex-col overflow-hidden">
            <span className="truncate text-[13px] font-semibold text-[#44403c]">
              Admin
            </span>
            <span className="truncate text-xs text-[#a8a29e]">
              System Owner
            </span>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

/* --------------------------------- Icons --------------------------------- */
// Inline SVGs so we don't introduce `lucide-react` just for six glyphs.
// Stroke geometry is the same as Feather / Lucide for visual parity with the
// wider Govard UI.

function iconBaseProps(props: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    ...props,
  };
}

function IconDashboard(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}

function IconDeals(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M3 7h18M6 7v13M18 7v13M3 20h18" />
      <path d="M9 11h3M9 15h3" />
    </svg>
  );
}

function IconContacts(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0Z" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

function IconTemplates(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
      <path d="M14 3v6h6M8 13h8M8 17h6" />
    </svg>
  );
}

function IconDocuments(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function IconHotLeads(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M12 2c1 3 4 5 4 9a4 4 0 1 1-8 0c0-2 1-3 2-4-1 3 2 3 2 0 0-2 0-3 0-5Z" />
    </svg>
  );
}

function IconSettings(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

function IconMemory(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M7 7h10v10H7z" />
      <path d="M4 10h1M4 14h1M19 10h1M19 14h1M10 4v1M14 4v1M10 19v1M14 19v1" />
    </svg>
  );
}

function IconRunCenter(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l2.5 2.5" />
    </svg>
  );
}

function IconScenario(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="9" y="14" width="6" height="6" rx="1" />
      <path d="M10 7h4M17 10v4M7 10v4" />
    </svg>
  );
}

function IconPromptCompiler(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M7 6h13M7 12h13M7 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function IconKnowledge(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M5 4h10a2 2 0 0 1 2 2v14H7a2 2 0 0 0-2 2" />
      <path d="M7 4v16" />
    </svg>
  );
}

function IconFiles(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="M3 7h6l2 2h10v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </svg>
  );
}

function IconAgents(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="8" r="3" />
      <path d="M4 20a4 4 0 0 1 8 0M12 20a4 4 0 0 1 8 0" />
    </svg>
  );
}

function IconOpencode(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <svg {...iconBaseProps(props)}>
      <path d="m8 9-4 3 4 3M16 9l4 3-4 3" />
      <path d="m14 5-4 14" />
    </svg>
  );
}
